import { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";

import {
  makeStyles,
  Grid,
  Button,
  Card,
  CardHeader,
  CardContent,
  CardActions
} from "@material-ui/core";

import {
  useStaffAuth0,
  useNonStaffAuth0,
  useAuth0
} from "src/components/AuthProvider";
import LoadingIndicator from "src/components/LoadingIndicator";
import palette from "src/styles/palette";
import useMousetrap from "src/util/useMousetrap";

const useStyles = makeStyles(_theme => ({
  container: {
    minHeight: "50vh"
  },
  disclaimer: {
    margin: "50px auto 0",
    maxWidth: 400,
    fontStyle: "italic",
    color: palette.darkGray
  }
}));

export default function Login(): JSX.Element {
  const [loading, setLoading] = useState(true);
  const classes = useStyles();
  const history = useHistory();
  const staffAuth0 = useStaffAuth0();
  const nonStaffAuth0 = useNonStaffAuth0();

  const urlParams = new URLSearchParams(window.location.search);
  const email = urlParams.get("email");
  const defaultToSignup = urlParams.get("hasAccount") === "false";
  const loginImmediately = urlParams.get("loginImmediately") === "true";
  const returnTo = urlParams.get("returnTo") || "/";
  const [staff, setStaff] = useState(urlParams.get("staff") === "true");

  const activeAuth0 = useAuth0();

  useMousetrap("mod+shift+s", e => {
    e.preventDefault();
    setStaff(!staff);
  });

  const onClick = useCallback(
    () =>
      (staff ? staffAuth0 : nonStaffAuth0).loginWithRedirect({
        authorizationParams: {
          screen_hint: defaultToSignup ? "signup" : "login",
          login_hint: email || ""
        },
        appState: {
          returnTo
        }
      }),
    [staff, staffAuth0, nonStaffAuth0, defaultToSignup, email, returnTo]
  );

  useEffect(() => {
    // Before we show the login form, we need to wait for auth0 to be ready.
    // Once auth0 is ready, we check if the user is already logged
    if (staffAuth0.isLoading || nonStaffAuth0.isLoading || !loading) {
      // either auth0 is still loading, OR loading==true, which means that
      // we already completed loading and auth0.isLoading transitioned back
      // to false for some reason.
      //
      // In either case, we don't need to do anything.
      return;
    }

    const loggedInEmail = activeAuth0.user?.email;

    // This is the first time auth0.isLoading is true -- figure out if the
    // user is already logged in, and, if so, whether they're logged in as
    // the correct user
    if (loggedInEmail) {
      // we're already logged in
      if (email && loggedInEmail !== email) {
        // we're logged in, but we're trying to log in as a different user
        activeAuth0
          .logout({
            logoutParams: {
              returnTo: `${window.location.origin}/login${window.location.search}`
            }
          })
          .catch(console.error);
      } else {
        // we're logged in already; just go to the homepage
        history.push("/");
      }
    } else {
      setLoading(false);

      if (loginImmediately) {
        void onClick();
      }
    }
  }, [
    activeAuth0,
    staffAuth0.isLoading,
    nonStaffAuth0.isLoading,
    email,
    history,
    loading,
    loginImmediately,
    onClick
  ]);

  return (
    <Grid
      container
      direction="column"
      alignItems="center"
      justify="center"
      className={classes.container}
    >
      {loading ? (
        <LoadingIndicator />
      ) : (
        <Grid item>
          <Card>
            <CardHeader title="Welcome" />
            <CardContent>
              Welcome to the Scale to Win Dialer admin dashboard.{" "}
              {defaultToSignup
                ? "Sign up to get started."
                : "Log in to get started."}
            </CardContent>
            <CardActions>
              <Button variant="contained" color="primary" onClick={onClick}>
                {staff ? "Staff Login" : defaultToSignup ? "Sign up" : "Log in"}
              </Button>
            </CardActions>
          </Card>
          <p className={classes.disclaimer}>
            By logging in, you agree to the Scale to Win{" "}
            <a
              href="https://scaletowin.com/terms-platform/"
              target="_new"
              rel="noopener noreferrer"
            >
              terms of service
            </a>{" "}
            and{" "}
            <a
              href="https://scaletowin.com/privacy-policy/"
              target="_new"
              rel="noopener noreferrer"
            >
              privacy policy
            </a>
            . Scale to Win may share your name, email, and phone number with
            this organization.
          </p>
        </Grid>
      )}
    </Grid>
  );
}
